import { Component, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { LLCFunctions } from '@functions/llc.service';
import { BusinessUserRole } from '@models/businesses';
import { Matter, MatterBusinessUser, MatterTitle } from '@models/matter';
import { MatterMortgageWithAssociations } from '@models/mortgage';
import {
	FCTSendData,
	MatterFCTResidentialTitleInsurance,
} from '@models/title-insurance/fct';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { FCTSelectors } from '@state/global/title-insurance/fct';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { MatterMortgagesState } from '@state/matter/matter_mortgages';
import LogRocket from 'logrocket';
import { take } from 'rxjs/operators';
import { LLCLoginComponent } from 'src/app/private/profile/integrations/llc/login/login.component';
import {
	PortalUnsavedChangesCloseData,
	PortalUnsavedChangesComponent,
} from 'src/app/private/sale/mortgages/llc/components/portal-unsaved-changes/portal-unsaved-changes.component';
import { ICON } from '@core/util/icon';
import { SnackbarService } from 'src/app/shared/services/ui/snackbar.service';
import { UrlService } from 'src/app/shared/services/url/url.service';
import { envTypes } from 'src/environments/env.types';
import { environment } from 'src/environments/environment';
import { FctGetStatusService } from '../../services/fct-get-status.service';
import { ValidateFctDataService } from '../../services/validate-fct-data.service';
import { MatterTitlesState } from '@state/matter/titles/titles.state';
import { Store } from '@ngxs/store';
import { BusinessUsersState } from '@state/business-users/business-users.state';
import { LeapDispatch } from 'src/app/public/leap/state/leap.dispatch';
import { TestHooks } from './fct-portal.hooks';

@UntilDestroy()
@Component({
	selector: 'app-fct-portal',
	templateUrl: './fct-portal.component.html',
	styleUrls: ['./fct-portal.component.scss'],
	standalone: false,
})
export class FctPortalComponent implements OnInit {
	testHooks = TestHooks;
	_url: SafeResourceUrl;
	unsafeUrl: string;
	loadingText: string;
	loading = true;
	sendingData = false;
	ICON = ICON;
	gettingStatus = false;

	public checkingAuth = false;
	public missingQuoteSections = [];
	public sendError: string;
	public fetchDataError = false;

	public backUrlParams = { 'back-string': 'Title insurance' };
	public closing = false;
	public requiresLawyerSignup = false;
	public integrationsProfileSetupUrl: string;

	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@SelectSnapshot(SelectedMatter.assignedLawyer)
	assignedLawyer: MatterBusinessUser;
	@SelectSnapshot(SelectedMatter.titleInsuranceThirdPartyId)
	thirdPartyId: string;
	@SelectSnapshot(SelectedMatter.getSelected) matter: Matter;
	@SelectSnapshot(MatterMortgagesState.getAllMortgagesWithAssociations)
	mortgages: MatterMortgageWithAssociations[];
	@SelectSnapshot(MatterTitlesState.getAll) matterTitles: MatterTitle[];
	@SelectSnapshot(FCTSelectors.getFCTDataPayload) fctDataPayload: FCTSendData;
	@SelectSnapshot(SelectedMatter.getTitleInsurance)
	titleInsurance: MatterFCTResidentialTitleInsurance;

	constructor(
		private dialog: MatDialog,
		private dialogRef: MatDialogRef<FctPortalComponent>,
		private sanitizer: DomSanitizer,
		private snackbarService: SnackbarService,
		private fctGetStatusService: FctGetStatusService,
		private validateFctDataService: ValidateFctDataService,
		private llcFunc: LLCFunctions,
		private store: Store,
		private leapDispatch: LeapDispatch,
	) {
		this.integrationsProfileSetupUrl = UrlService.Profile_Integrations_FCT();
		this.dialogRef.disableClose = true;
		this.dialogRef.addPanelClass(['vw-90', 'vh-90']);

		this.dialogRef
			.backdropClick()
			.pipe(untilDestroyed(this))
			.subscribe(() => {
				this.beforeClose();
			});

		window.onmessage = async (ev: MessageEvent<unknown>) => {
			try {
				if (ev?.data === 'dirty') {
					this.loading = false;
					const unsavedChangesDialog = this.dialog.open(
						PortalUnsavedChangesComponent,
						{},
					);
					unsavedChangesDialog
						.afterClosed()
						.subscribe(async (unsavedData: PortalUnsavedChangesCloseData) => {
							if (!unsavedData.stayOnPage) {
								await this.syncData();
							}
						});
				} else if (ev?.data === 'untouched') {
					await this.syncData();
				}
			} catch (err) {
				LogRocket.log('FCT - Error closing portal', err);
				console.log('Error closing portal', err);
			} finally {
				this.closing = false;
			}
		};
	}

	async ngOnInit() {
		await this.checkAuthAndSendData();
	}

	async checkAuthAndSendData() {
		let authenticated;
		this.sendError = null;

		try {
			this.checkingAuth = true;
			authenticated = await this.llcFunc
				.checkUserConnected(this.matter.lawyerAssigned.id)
				.pipe(take(1))
				.toPromise();

			if (!authenticated) {
				if (
					this.store.selectSnapshot(BusinessUsersState.getLoggedInUserRole) ===
					BusinessUserRole.CLERK
				) {
					this.requiresLawyerSignup = true;

					return;
				}

				const _dialogRef = this.dialog.open(LLCLoginComponent, {
					maxWidth: '550px',
					width: '100%',
					data: { type: 'FCT' },
				});
				_dialogRef
					.afterClosed()
					.pipe(untilDestroyed(this))
					.subscribe(async (result) => {
						if (result) {
							await this.buildDataAfterAuth();
						} else {
							this.dialogRef.close();
						}
					});
			}
		} catch (err) {
			console.log('FCT - Unable to check auth', err);
			LogRocket.log('FCT - unable to check authentication', err);
			this.sendError = 'Unable to validate login details with FCT.';
		} finally {
			this.checkingAuth = false;
		}

		try {
			if (!this.sendError && authenticated) {
				await this.buildDataAfterAuth();
			}
		} catch (err) {
			LogRocket.log('FCT - Error when sending data', err);
			console.log('FCT - Error when sending data', err);
			this.sendError = 'There was an error when sending data to FCT.';
		}
	}

	async buildDataAfterAuth() {
		this.missingQuoteSections =
			this.validateFctDataService.getQuoteMissingSections(
				this.matter,
				this.mortgages,
				this.matterTitles,
			);
		if (this.missingQuoteSections?.length === 0) {
			await this._sendData();
		}
	}

	async _sendData() {
		this.sendError = null;
		this.sendingData = true;

		if (this.fctDataPayload) {
			if (environment.env !== envTypes.PRODUCTION) {
				console.log('Payload sent to FCT', this.fctDataPayload);
			}
			LogRocket.log('Payload sent to FCT portal', this.fctDataPayload);
			try {
				const { matterId } = this;
				const result = await this.fctGetStatusService.updateFCTData(
					matterId,
					this.assignedLawyer?.id,
					this.titleInsurance,
					this.fctDataPayload,
					this.fctDataPayload,
				);

				if (this.matterId === result.matterId) {
					this.setUrl(result.url);
				}
			} catch (err) {
				console.log('Error submitting data to FCT', err);
				this.sendError =
					'There was an error submitting the data to FCT. Please close the modal and retry.';
				this.snackbarService.openSnackBar({
					text: 'There was an error submitting the data to FCT. Please retry.',
					type: 'error',
				});
				LogRocket.log('Error submitting data to FCT', err);
			} finally {
				this.sendingData = false;
			}
		}
	}

	openUrl() {
		this.dialogRef.close();
	}

	beforeClose() {
		if (!this.closing) {
			try {
				this.closing = true;
				const iframe = document.querySelector('#llc-iframe');
				if (iframe) {
					(iframe as HTMLIFrameElement).contentWindow.postMessage('OK', '*');
					this.loadingText = 'Checking LLC for unsaved changes.';
				} else {
					this.forceClose();
				}
			} catch (err) {
				LogRocket.log('FCT - Error checking any changes from FCT modal', err);
				console.log('Error checking any changes from FCT modal', err);
			}
		}
	}

	setUrl(_url: string) {
		if (_url) {
			this._url = this.sanitizer.bypassSecurityTrustResourceUrl(_url);
			this.unsafeUrl = _url;
		}
	}

	async syncData() {
		this.fetchDataError = false;
		this.gettingStatus = true;
		try {
			const { matterId, thirdPartyId } = this;
			await this.fctGetStatusService.updateStatus(matterId, thirdPartyId);
		} catch (err) {
			this.fetchDataError = true;
			console.log('Error syncing data from FCT', err);
			this.snackbarService.openSnackBar({
				text: 'There was an error fetching data from FCT. Please open the portal again to get the latest data.',
				type: 'error',
			});
			LogRocket.log('Error submitting data to FCT', err);
		} finally {
			this.gettingStatus = false;
			this.forceClose();
		}
	}

	forceClose() {
		this.dialogRef.close();
	}
}
